import React, { useRef, useEffect } from 'react'
import LyricBlockEditor from './LyricBlockEditor'

const DEFAULT_VIEW_START_SECONDS = 0

/**
 * TimelineTrack Component
 * Container for lyric blocks with grid background and scrolling
 */
const TimelineTrack = ({
  lyrics,
  duration,
  currentTime,
  currentLyricIndex,
  selectedBlockIndex,
  zoom,
  snapToGrid,
  timeToPixels,
  pixelsToTime,
  snapTime,
  onSelectBlock,
  onEditBlock,
  onUpdateBlock,
  onScrollChange
}) => {
  const trackRef = useRef(null)
  const scrollContainerRef = useRef(null)
  const initialScrollAppliedRef = useRef(false)

  // Calculate total width based on duration and zoom
  const totalWidth = timeToPixels(duration, zoom) + 100 // Add padding

  useEffect(() => {
    initialScrollAppliedRef.current = false
  }, [duration])

  // Apply a default scroll position so the initial view starts at 30 seconds
  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container || initialScrollAppliedRef.current) {
      return
    }

    const containerWidth = container.clientWidth
    if (containerWidth <= 0) {
      initialScrollAppliedRef.current = true
      return
    }

    const desiredScrollLeft = timeToPixels(DEFAULT_VIEW_START_SECONDS, zoom)
    const maxScrollLeft = Math.max(0, container.scrollWidth - containerWidth)
    const clampedScrollLeft = Math.min(desiredScrollLeft, maxScrollLeft)

    if (clampedScrollLeft > 0) {
      container.scrollLeft = clampedScrollLeft
      if (onScrollChange) {
        onScrollChange(clampedScrollLeft)
      }
    }

    initialScrollAppliedRef.current = true
  }, [zoom, timeToPixels, onScrollChange, duration])

  // Auto-scroll to keep playhead in view
  useEffect(() => {
    if (scrollContainerRef.current && currentTime >= 0) {
      if (!initialScrollAppliedRef.current) {
        return
      }
      const playheadPosition = timeToPixels(currentTime, zoom)
      const container = scrollContainerRef.current
      const scrollLeft = container.scrollLeft
      const containerWidth = container.clientWidth

      // Keep playhead in center third of viewport
      const leftThreshold = scrollLeft + containerWidth * 0.33
      const rightThreshold = scrollLeft + containerWidth * 0.66

      if (playheadPosition < leftThreshold || playheadPosition > rightThreshold) {
        const newScrollLeft = playheadPosition - containerWidth / 2
        container.scrollTo({
          left: Math.max(0, newScrollLeft),
          behavior: 'smooth'
        })
      }
    }
  }, [currentTime, zoom, timeToPixels])

  // Handle scroll
  const handleScroll = (e) => {
    if (onScrollChange) {
      onScrollChange(e.target.scrollLeft)
    }
  }

  // Click on empty space to deselect
  const handleTrackClick = (e) => {
    if (e.target === trackRef.current || e.target === scrollContainerRef.current) {
      onSelectBlock(null)
    }
  }

  return (
    <div
      ref={scrollContainerRef}
      className="relative h-32 overflow-x-auto overflow-y-hidden bg-black/20 max-w-full min-w-0"
      onScroll={handleScroll}
      onClick={handleTrackClick}
    >
      <div
        ref={trackRef}
        className="relative h-full"
        style={{
          width: `${totalWidth}px`,
          backgroundImage: snapToGrid
            ? `repeating-linear-gradient(
                to right,
                rgba(255, 255, 255, 0.05) 0px,
                rgba(255, 255, 255, 0.05) 1px,
                transparent 1px,
                transparent ${timeToPixels(1, zoom)}px
              )`
            : 'none'
        }}
      >
        {/* Render lyric blocks - with defensive check */}
        {Array.isArray(lyrics) && lyrics.map((lyric, index) => {
          const isSelected = index === selectedBlockIndex
          const isCurrent = index === currentLyricIndex
          const isPast = index < currentLyricIndex

          return (
            <LyricBlockEditor
              key={index}
              lyric={lyric}
              index={index}
              isSelected={isSelected}
              isCurrent={isCurrent}
              isPast={isPast}
              timeToPixels={timeToPixels}
              pixelsToTime={pixelsToTime}
              zoom={zoom}
              snapTime={snapTime}
              onSelect={onSelectBlock}
              onDoubleClick={onEditBlock}
              onDrag={(idx, newTimes) => {
                // Update lyric with new times
                onUpdateBlock(idx, newTimes)
              }}
              onResize={(idx, newTimes) => {
                // Update lyric with new times
                onUpdateBlock(idx, newTimes)
              }}
            />
          )
        })}

        {/* Empty state */}
        {(!Array.isArray(lyrics) || lyrics.length === 0) && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center text-white/40">
              <p className="text-lg font-medium">No lyrics yet</p>
              <p className="text-sm mt-1">Click "Add Lyric" to create a new one</p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default TimelineTrack
